import {z} from 'zod';
import {uniqBy} from 'lodash-es';
import {Sorting} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    composeComparators,
    keyComparator,
    zDateTimeAsSent,
} from '@brunata-angular/util';

export const archivedBillSchema = z.object({
    documentLink: z.string(),
    cutOffDate: zDateTimeAsSent,
    /** The identifier printed on the bill, shown as stored rather than read as a date. */
    version: z.string().nullable().optionalUntilDeployed('AGI-20025'),
    releasedForConsumer: z.boolean(),
    // Older archived bills carry no account type.
    accountType: z.string().nullable(),
});

export type ArchivedBill = z.infer<typeof archivedBillSchema>;

/** The archive can hold the same document twice for one debtor; the card lists it once. */
export function uniqueBills(bills: readonly ArchivedBill[]): ArchivedBill[] {
    return uniqBy(bills, bill => bill.documentLink);
}

/** Newest version first, as on the React screen. */
export const defaultBillSorting: Sorting = {column: 'version', direction: 'desc'};

/** A bill without a version counts as the oldest. */
const byVersion = (
    direction: Sorting['direction'],
): ((first: ArchivedBill, second: ArchivedBill) => number) =>
    keyComparator(bill => bill.version, direction, direction === 'asc' ? 'first' : 'last');

/** Rows that tie on the chosen column keep the newest version first. */
export function sortBillRows(
    bills: readonly ArchivedBill[],
    sorting: Sorting,
    accountTypeLabel: (accountType: string) => string,
): ArchivedBill[] {
    return [...bills].sort(
        composeComparators([billComparator(sorting, accountTypeLabel), byVersion('desc')]),
    );
}

function billComparator(
    sorting: Sorting,
    accountTypeLabel: (accountType: string) => string,
): (first: ArchivedBill, second: ArchivedBill) => number {
    switch (sorting.column) {
        case 'cutOffDate':
            return keyComparator(bill => bill.cutOffDate.toMillis(), sorting.direction);
        case 'accountType':
            // A bill without an account type sorts after every label.
            return composeComparators<ArchivedBill>([
                keyComparator(bill => (bill.accountType == null ? 1 : 0), sorting.direction),
                caseInsensitiveComparator(
                    bill => (bill.accountType == null ? '' : accountTypeLabel(bill.accountType)),
                    sorting.direction,
                ),
            ]);
        default:
            return byVersion(sorting.direction);
    }
}

/**
 * The endpoint hands out an absolute link into the archive webservice of the host it runs on. The
 * archive service here already knows that host, so only the document id at the end is needed.
 */
export function documentIdFromLink(documentLink: string): string | null {
    const documentId = documentLink.split('/').pop();
    return documentId != null && documentId !== '' ? documentId : null;
}
